import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  LoginCredentials,
  AuthResponse,
  ParkingSpace,
  Vehicle,
  ParkingRecord,
  Payment,
  DashboardStats,
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  RevenueData,
  OccupancyData
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 調試輸出目前使用的後端 API 地址
    try {
      // 僅在開發模式下打印，避免污染正式環境日誌
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.info('[ApiService] baseURL =', this.api.defaults.baseURL);
      }
    } catch {}

    // 請求攔截器 - 添加認證token（容錯讀取 token/authToken，localStorage 與 sessionStorage）
    this.api.interceptors.request.use(
      (config) => {
        const token =
          localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          sessionStorage.getItem('token') ||
          sessionStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 響應攔截器 - 處理錯誤和token過期
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token過期，清除本地存儲並重定向到登入頁面
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // 認證相關API
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.api.post('/auth/login', credentials);
    const data = response.data;
    
    // 處理後端響應格式，將 data 中的 token 和 user 提取到頂層
    if (data.success && data.data) {
      return {
        success: data.success,
        token: data.data.token,
        user: data.data.user,
        message: data.message
      };
    }
    
    return data;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.api.post<ApiResponse>('/auth/logout');
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.api.get('/auth/me');
    const data = response.data;

    // 後端返回格式為 { success, data: { user } }，這裡展平成 { success, data: user }
    if (data?.success && data?.data?.user) {
      return {
        success: data.success,
        data: data.data.user,
        message: data.message,
      };
    }

    return data;
  }

  // 儀表板相關API
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.api.get<ApiResponse<DashboardStats>>('/dashboard/stats');
    return response.data;
  }

  async getRevenueData(days: number = 7): Promise<ApiResponse<RevenueData[]>> {
    const response = await this.api.get<ApiResponse<RevenueData[]>>(`/reports/revenue?days=${days}`);
    return response.data;
  }

  async getOccupancyData(): Promise<ApiResponse<OccupancyData[]>> {
    const response = await this.api.get<ApiResponse<OccupancyData[]>>('/reports/occupancy');
    return response.data;
  }

  // 報表相關API
  async getReportsDashboard(): Promise<ApiResponse<any>> {
    const response = await this.api.get<ApiResponse<any>>('/reports/dashboard');
    return response.data;
  }

  async getReports(params?: PaginationParams): Promise<ApiResponse<any>> {
    const response = await this.api.get<ApiResponse<any>>('/reports', { params });
    return response.data;
  }

  // 停車位相關API
  async getParkingSpaces(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<ParkingSpace>>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<ParkingSpace>>>('/parking/spaces', { params });
    return response.data;
  }

  async getParkingSpace(id: string): Promise<ApiResponse<ParkingSpace>> {
    const response = await this.api.get<ApiResponse<ParkingSpace>>(`/parking/spaces/${id}`);
    return response.data;
  }

  async createParkingSpace(space: Omit<ParkingSpace, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<ParkingSpace>> {
    const response = await this.api.post<ApiResponse<ParkingSpace>>('/parking/spaces', space);
    return response.data;
  }

  async updateParkingSpace(id: string, space: Partial<ParkingSpace>): Promise<ApiResponse<ParkingSpace>> {
    const response = await this.api.put<ApiResponse<ParkingSpace>>(`/parking/spaces/${id}`, space);
    return response.data;
  }

  async deleteParkingSpace(id: string): Promise<ApiResponse> {
    const response = await this.api.delete<ApiResponse>(`/parking/spaces/${id}`);
    return response.data;
  }

  // 車輛相關API
  async getVehicles(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Vehicle>>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<Vehicle>>>('/vehicles', { params });
    return response.data;
  }

  async getVehicle(id: string): Promise<ApiResponse<Vehicle>> {
    const response = await this.api.get<ApiResponse<Vehicle>>(`/vehicles/${id}`);
    return response.data;
  }

  async createVehicle(vehicle: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Vehicle>> {
    const response = await this.api.post<ApiResponse<Vehicle>>('/vehicles', vehicle);
    return response.data;
  }

  async updateVehicle(id: string, vehicle: Partial<Vehicle>): Promise<ApiResponse<Vehicle>> {
    const response = await this.api.put<ApiResponse<Vehicle>>(`/vehicles/${id}`, vehicle);
    return response.data;
  }

  async deleteVehicle(id: string): Promise<ApiResponse> {
    const response = await this.api.delete<ApiResponse>(`/vehicles/${id}`);
    return response.data;
  }

  // 車輛匯入相關API
  async importVehicles(file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post<ApiResponse<any>>('/vehicle-registration/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async downloadVehicleTemplate(format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const response = await this.api.get(`/vehicle-registration/template?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // 停車記錄相關API
  async getParkingRecords(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<ParkingRecord>>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<ParkingRecord>>>('/parking/records', { params });
    return response.data;
  }

  async getParkingRecord(id: string): Promise<ApiResponse<ParkingRecord>> {
    const response = await this.api.get<ApiResponse<ParkingRecord>>(`/parking/records/${id}`);
    return response.data;
  }

  async createParkingRecord(record: { vehicleId: string; spaceId: string }): Promise<ApiResponse<ParkingRecord>> {
    const response = await this.api.post<ApiResponse<ParkingRecord>>('/parking/entry', record);
    return response.data;
  }

  async exitParking(recordId: string): Promise<ApiResponse<ParkingRecord>> {
    const response = await this.api.post<ApiResponse<ParkingRecord>>(`/parking/exit/${recordId}`);
    return response.data;
  }

  // 在場車輛 / 停車會話相關API
  async getParkingSessions(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/parking/sessions/active', { params });
    return response.data;
  }

  // 支付相關API
  async getPayments(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Payment>>> {
    try {
      const response = await this.api.get<ApiResponse<PaginatedResponse<Payment>>>('/payments', { params });
      
      // 處理不同的響應格式
      if (response.data.success) {
        // 如果後端返回的數據不是分頁格式，轉換為分頁格式
        if (Array.isArray(response.data.data)) {
          const items = response.data.data;
          return {
            success: true,
            data: {
              items: items,
              pagination: {
                page: params?.page || 1,
                limit: params?.limit || 10,
                total: items.length,
                totalPages: Math.ceil(items.length / (params?.limit || 10))
              }
            }
          };
        }
        return response.data;
      } else {
        return {
          success: false,
          message: response.data.message || '獲取支付數據失敗',
          data: {
            items: [],
            pagination: {
              page: 1,
              limit: 10,
              total: 0,
              totalPages: 0
            }
          }
        };
      }
    } catch (error: any) {
      console.error('API請求失敗:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || '網絡請求失敗',
        data: {
          items: [],
          pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0
          }
        }
      };
    }
  }

  async createPayment(payment: { recordId: string; amount: number; method: Payment['method'] }): Promise<ApiResponse<Payment>> {
    const response = await this.api.post<ApiResponse<Payment>>('/payments', payment);
    return response.data;
  }

  async updatePaymentStatus(id: string, status: Payment['status']): Promise<ApiResponse<Payment>> {
    const response = await this.api.put<ApiResponse<Payment>>(`/payments/${id}/status`, { status });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;